import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { AdminMetrika } from '../api/adminMetrikaApi';
import type { AdminProduct, Cohort, ModulesStats, ProductStep } from '../api/adminProductApi';
import { fmtCalendarDay, fmtInt, fmtPct } from '../model/format';
import { BarList, Panel, Tile } from './AdminAnalytics';

/** Малые доли — с десятыми: 2 из 456 это 0,4%, а не «0%». */
const share = (part: number, whole: number) => {
  if (!whole) return '—';
  const value = part / whole;
  return value > 0 && value < 0.1
    ? `${(value * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
    : fmtPct(value);
};

/** Ячейка когорты: фон по доле вернувшихся (один тон, чем больше — тем ярче). */
function CohortCell({ returned, eligible }: { returned: number; eligible: number }) {
  if (!eligible) return <td className="px-2 py-2 text-center text-muted">—</td>;
  const value = returned / eligible;
  return (
    <td
      className="px-2 py-2 text-center tabular-nums"
      style={{ background: `rgba(57, 135, 229, ${0.08 + value * 0.62})` }}
      title={`${returned} / ${eligible}`}
    >
      <span className="font-semibold text-fg">{share(returned, eligible)}</span>
      <span className="ml-1 text-[11px] text-muted">{fmtInt(returned)}</span>
    </td>
  );
}

function CohortTable({ cohorts, lang }: { cohorts: Cohort[]; lang: string }) {
  const { t } = useTranslation();
  const points = cohorts[0]?.cells.map((cell) => cell.day) ?? [];
  const rows = [...cohorts].reverse();
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[560px] border-separate border-spacing-1 text-[13px]">
        <thead>
          <tr className="text-left text-[12px] text-muted">
            <th className="px-2 font-medium">{t('admin.product.cohorts.week')}</th>
            <th className="px-2 text-right font-medium">{t('admin.product.cohorts.size')}</th>
            {points.map((day) => (
              <th key={day} className="px-2 text-center font-medium">
                {t('admin.product.cohorts.day', { n: day })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const [y, m, d] = row.weekStart.split('-').map(Number);
            const end = new Date(y, m - 1, d + 6);
            const endIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
            return (
              <tr key={row.weekStart}>
                <td className="whitespace-nowrap px-2 py-2 text-fg">
                  {fmtCalendarDay(row.weekStart, lang)} – {fmtCalendarDay(endIso, lang)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-fg">{fmtInt(row.size)}</td>
                {row.cells.map((cell) => (
                  <CohortCell key={cell.day} returned={cell.returned} eligible={cell.eligible} />
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Модули за период: свои, готовые по способу добавления, топ каталога, слова, удаления (2026-10-21). */
function ModulesPanel({ modules, days }: { modules: ModulesStats; days: number }) {
  const { t } = useTranslation();
  const addedTotal = modules.added.reduce((total, row) => total + row.count, 0);
  const line = (label: string, users: number | null, count: number) => (
    <tr className="border-t border-line">
      <td className="py-2 pr-3 text-fg">{label}</td>
      <td className="py-2 text-right font-semibold tabular-nums text-fg">{users === null ? '' : fmtInt(users)}</td>
      <td className="py-2 text-right tabular-nums text-muted">{fmtInt(count)}</td>
    </tr>
  );
  return (
    <Panel title={t('admin.product.modules.title')} hint={t('admin.product.modules.hint', { days })}>
      <div className="grid gap-5 lg:grid-cols-2">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="text-left text-[12px] text-muted">
              <th className="pb-2 font-medium" />
              <th className="pb-2 text-right font-medium">{t('admin.product.features.people')}</th>
              <th className="pb-2 text-right font-medium">{t('admin.product.modules.count')}</th>
            </tr>
          </thead>
          <tbody>
            {line(t('admin.product.modules.created'), modules.createdUsers, modules.created)}
            {line(t('admin.product.modules.added'), null, addedTotal)}
            {modules.added.map((row) => (
              <tr key={row.via} className="text-[13px]">
                <td className="py-1 pl-4 pr-3 text-muted">{t(`admin.product.modules.via.${row.via}`, row.via)}</td>
                <td className="py-1 text-right tabular-nums text-muted">{fmtInt(row.users)}</td>
                <td className="py-1 text-right tabular-nums text-muted">{fmtInt(row.count)}</td>
              </tr>
            ))}
            {line(t('admin.product.modules.words'), modules.wordsUsers, modules.words)}
            <tr className="text-[13px]">
              <td className="py-1 pl-4 pr-3 text-muted">{t('admin.product.modules.wordsOwn')}</td>
              <td />
              <td className="py-1 text-right tabular-nums text-muted">{fmtInt(modules.wordsOwn)}</td>
            </tr>
            <tr className="text-[13px]">
              <td className="py-1 pl-4 pr-3 text-muted">{t('admin.product.modules.wordsCopies')}</td>
              <td />
              <td className="py-1 text-right tabular-nums text-muted">{fmtInt(modules.wordsCopies)}</td>
            </tr>
            {line(t('admin.product.modules.deleted'), null, modules.deleted)}
          </tbody>
        </table>
        <div>
          <div className="mb-2 text-[12px] text-muted">{t('admin.product.modules.top')}</div>
          {modules.top.length === 0 ? (
            <p className="text-[14px] text-muted">{t('admin.product.modules.topEmpty')}</p>
          ) : (
            <BarList
              total={modules.top[0]?.adds ?? 0}
              rows={modules.top.map((row) => ({ label: row.title, value: row.adds }))}
            />
          )}
        </div>
      </div>
    </Panel>
  );
}

/** Яндекс Метрика: откуда приходят посетители и сколько регистрируются (2026-10-21). */
export function MetrikaPanel({ data, days }: { data: AdminMetrika | undefined; days: number }) {
  const { t } = useTranslation();
  const table = (rows: AdminMetrika['sources'], head: string) => (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-left text-[12px] text-muted">
          <th className="pb-2 font-medium">{head}</th>
          <th className="pb-2 text-right font-medium">{t('admin.product.metrika.visits')}</th>
          <th className="pb-2 text-right font-medium">{t('admin.product.metrika.users')}</th>
          <th className="pb-2 text-right font-medium">{t('admin.product.metrika.registrations')}</th>
          <th className="pb-2 text-right font-medium">{t('admin.product.metrika.conversion')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.name} className="border-t border-line">
            <td className="max-w-[220px] truncate py-2 pr-3 text-fg">{row.name}</td>
            <td className="py-2 text-right tabular-nums text-fg">{fmtInt(row.visits)}</td>
            <td className="py-2 text-right tabular-nums text-muted">{fmtInt(row.users)}</td>
            <td className="py-2 text-right font-semibold tabular-nums text-fg">
              {row.registrations === null ? '—' : fmtInt(row.registrations)}
            </td>
            <td className="py-2 text-right tabular-nums text-muted">
              {row.registrations === null ? '—' : share(row.registrations, row.users)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Panel
      title={t('admin.product.metrika.title')}
      hint={
        data?.configured && !data.error
          ? t('admin.product.metrika.hint', {
              days,
              visits: fmtInt(data.visits),
              users: fmtInt(data.users),
              registrations: data.registrations === null ? '—' : fmtInt(data.registrations),
            })
          : undefined
      }
    >
      {!data ? (
        <div className="h-24 animate-skeleton rounded-[12px] bg-elev" />
      ) : !data.configured ? (
        <p className="text-[14px] text-muted">{t('admin.product.metrika.off')}</p>
      ) : data.error ? (
        <p className="text-[14px] text-muted">{t('admin.product.metrika.error')}</p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {table(data.sources, t('admin.product.metrika.source'))}
          {data.utm.length > 0 ? table(data.utm, 'utm_source') : <p className="text-[14px] text-muted">{t('admin.product.metrika.noUtm')}</p>}
        </div>
      )}
    </Panel>
  );
}

/** «Доиграл из начатых»: 1/2; ничего не начато — прочерк. */
function doneOf(done: number, started: number) {
  return started ? `${fmtInt(done)}/${fmtInt(started)}` : '—';
}

function sinceNote(step: ProductStep, periodStart: string, lang: string, t: (k: string, o?: Record<string, unknown>) => string) {
  return step.trackedSince && step.trackedSince > periodStart
    ? t('admin.product.since', { date: fmtCalendarDay(step.trackedSince, lang, 'long') })
    : step.trackedSince === null && ['card_tried', 'card_created', 'topics_opened', 'plans_opened', 'game_opened', 'daily_task_taken'].includes(step.key)
      ? t('admin.product.notTracked')
      : undefined;
}

/**
 * Аналитика учеников (решение владельца 2026-10-21, «пользователи не возвращаются»): удержание по неделям,
 * через сколько возвращаются, воронка новых, функции, письма-напоминания и сами новые ученики.
 */
export function AdminProductView({ data, metrika }: { data: AdminProduct; metrika?: AdminMetrika }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ru';
  const [onlyLost, setOnlyLost] = useState(false);
  const { days } = data;
  const generated = new Date(data.generatedAt);
  const start = new Date(generated.getTime() + 3 * 3600 * 1000 - (days - 1) * 86400 * 1000);
  const periodStart = start.toISOString().slice(0, 10);
  const registered = data.funnel.find((step) => step.key === 'registered')?.users ?? 0;
  const users = onlyLost ? data.users.filter((user) => !user.returned) : data.users;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label={t('admin.product.tiles.registered', { days })} value={fmtInt(data.registered)} />
        <Tile
          label={t('admin.product.tiles.returned')}
          value={share(data.returned, data.returnedBase)}
          sub={t('admin.product.tiles.returnedSub', { n: fmtInt(data.returned), base: fmtInt(data.returnedBase) })}
        />
        <Tile
          label={t('admin.product.tiles.median')}
          value={
            data.medianReturnDays === null
              ? '—'
              : t('admin.product.tiles.medianValue', { n: data.medianReturnDays.toLocaleString('ru-RU') })
          }
          sub={t('admin.product.tiles.medianSub')}
        />
        <Tile
          label={t('admin.product.tiles.events')}
          value={data.eventsSince ? fmtCalendarDay(data.eventsSince, lang, 'long') : '—'}
          sub={data.eventsSince ? t('admin.product.tiles.eventsSub') : t('admin.product.tiles.eventsNone')}
        />
      </div>

      <MetrikaPanel data={metrika} days={days} />

      <Panel title={t('admin.product.cohorts.title')} hint={t('admin.product.cohorts.hint')}>
        <CohortTable cohorts={data.cohorts} lang={lang} />
      </Panel>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={t('admin.product.buckets.title')} hint={t('admin.product.buckets.hint', { days })}>
          <BarList
            total={data.returnedBase}
            rows={data.returnBuckets.map((bucket) => ({
              label: t(`admin.product.buckets.${bucket.key}`),
              value: bucket.users,
            }))}
          />
        </Panel>

        <Panel title={t('admin.product.funnel.title')} hint={t('admin.product.funnel.hint', { days })}>
          <BarList
            total={registered}
            rows={data.funnel.map((step) => ({
              label: t(`admin.product.funnel.${step.key}`),
              value: step.users,
              note: sinceNote(step, periodStart, lang, t),
            }))}
          />
        </Panel>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={t('admin.product.features.title')} hint={t('admin.product.features.hint', { days })}>
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left text-[12px] text-muted">
                <th className="pb-2 font-medium" />
                <th className="pb-2 text-right font-medium">{t('admin.product.features.people')}</th>
                <th className="pb-2 text-right font-medium">{t('admin.product.features.times')}</th>
              </tr>
            </thead>
            <tbody>
              {data.features.map((row) => {
                const note = sinceNote(row, periodStart, lang, t);
                return (
                  <tr key={row.key} className="border-t border-line">
                    <td className="py-2 pr-3 text-fg">
                      {t(`admin.product.features.${row.key}`)}
                      {note && <div className="text-[12px] text-muted">{note}</div>}
                    </td>
                    <td className="py-2 text-right font-semibold tabular-nums text-fg">{fmtInt(row.users)}</td>
                    <td className="py-2 text-right tabular-nums text-muted">{fmtInt(row.times ?? 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>

        <Panel title={t('admin.product.emails.title')} hint={t('admin.product.emails.hint', { days })}>
          {data.emails.length === 0 ? (
            <p className="text-[14px] leading-relaxed text-muted">{t('admin.product.emails.empty')}</p>
          ) : (
            <div className="-mx-1 overflow-x-auto">
              <table className="w-full min-w-[520px] text-[13px]">
                <thead>
                  <tr className="text-left text-[12px] text-muted">
                    <th className="px-1 pb-2 font-medium">{t('admin.product.emails.kind')}</th>
                    <th className="px-1 pb-2 text-right font-medium">{t('admin.product.emails.sent')}</th>
                    <th className="px-1 pb-2 text-right font-medium">{t('admin.product.emails.clicked')}</th>
                    <th className="px-1 pb-2 text-right font-medium">{t('admin.product.emails.returned')}</th>
                    <th className="px-1 pb-2 text-right font-medium">{t('admin.product.emails.trained')}</th>
                    <th className="px-1 pb-2 text-right font-medium" title={t('admin.product.emails.holdoutHint')}>
                      {t('admin.product.emails.holdout')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.emails.map((row) => (
                    <tr key={row.kind} className="border-t border-line">
                      <td className="px-1 py-2 text-fg">{t(`admin.product.emails.kinds.${row.kind}`, row.kind)}</td>
                      <td className="px-1 py-2 text-right tabular-nums text-fg">{fmtInt(row.sent)}</td>
                      <td className="px-1 py-2 text-right tabular-nums">{share(row.clicked, row.sent)}</td>
                      <td className="px-1 py-2 text-right font-semibold tabular-nums text-fg">
                        {share(row.returned, row.sent)}
                      </td>
                      <td className="px-1 py-2 text-right tabular-nums">{share(row.trained, row.sent)}</td>
                      <td className="px-1 py-2 text-right tabular-nums text-muted">
                        {row.holdout ? `${share(row.holdoutReturned, row.holdout)} · ${fmtInt(row.holdout)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {data.modules && <ModulesPanel modules={data.modules} days={days} />}

      <Panel
        title={t('admin.product.users.title')}
        hint={
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={onlyLost} onChange={(event) => setOnlyLost(event.target.checked)} />
            {t('admin.product.users.onlyLost')}
            <span>· {t('admin.product.users.count', { shown: fmtInt(users.length), total: fmtInt(data.usersTotal), days })}</span>
          </label>
        }
      >
        <div className="-mx-1 max-h-[560px] overflow-auto">
          <table className="w-full min-w-[1410px] text-[13px]">
            <thead className="sticky top-0 bg-card">
              <tr className="text-left text-[12px] text-muted">
                {(['date', 'user', 'source', 'onboarding', 'verified', 'presses', 'modules', 'words', 'batches', 'trainings', 'game', 'stories', 'missions', 'boss', 'dailyTask', 'days', 'last', 'plan'] as const).map(
                  (column) => (
                    <th
                      key={column}
                      className={cn('px-1.5 pb-2 font-medium', !['date', 'user', 'source', 'plan'].includes(column) && 'text-right')}
                      title={column === 'boss' ? t('admin.product.users.bossHint') : undefined}
                    >
                      {t(`admin.product.users.${column}`)}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={cn('border-t border-line', !user.returned && 'text-muted')}>
                  <td className="whitespace-nowrap px-1.5 py-2">{fmtCalendarDay(user.registeredAt.slice(0, 10), lang)}</td>
                  <td className="max-w-[240px] px-1.5 py-2">
                    <div className="truncate text-fg">{user.name}</div>
                    <div className="truncate text-[12px] text-muted">{user.email}</div>
                  </td>
                  <td className="max-w-[140px] truncate px-1.5 py-2">{user.source}</td>
                  <td className="px-1.5 py-2 text-right">{user.onboarding ? '✓' : '—'}</td>
                  <td className="px-1.5 py-2 text-right">{user.verified ? '✓' : '—'}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{fmtInt(user.cardPresses)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{fmtInt(user.modulesCreated)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{fmtInt(user.wordsAdded)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{fmtInt(user.topicBatches)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{fmtInt(user.trainings)}</td>
                  <td className="px-1.5 py-2 text-right">{user.gameOpened ? '✓' : '—'}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{doneOf(user.storiesFinished, user.storiesStarted)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{doneOf(user.missionsFinished, user.missionsStarted)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{doneOf(user.bossWon, user.bossStarted)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{doneOf(user.dailyTasksDone, user.dailyTasksTaken)}</td>
                  <td className="px-1.5 py-2 text-right tabular-nums">{fmtInt(user.activeDays)}</td>
                  <td className="whitespace-nowrap px-1.5 py-2 text-right">
                    {user.lastActive ? fmtCalendarDay(user.lastActive, lang) : '—'}
                  </td>
                  <td className="px-1.5 py-2">{user.plan === 'PREMIUM' ? 'Max' : user.plan === 'PRO' ? 'Pro' : 'Demo'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
